import type { MDXComponents } from "mdx/types";
import Image from "next/image";
import Link from "next/link";
import React from "react";

export const components = {
	h1: createHeading(1),
	h2: createHeading(2),
	h3: createHeading(3),
	h4: createHeading(4),
	h5: createHeading(5),
	h6: createHeading(6),
	Image: RoundedImage,
	a: CustomLink,
	Table,
	p: Paragraph,
	li: ListItem,
	ul: List,
	strong: Strong,
} satisfies MDXComponents;

export function useMDXComponents(): MDXComponents {
	return components;
}

function Table({ data }: { data: { headers: string[]; rows: string[][] } }) {
	const headers = data.headers.map((header) => <th key={header}>{header}</th>);
	const rows = data.rows.map((row) => (
		<tr key={row.join("-")}>
			{row.map((cell) => (
				<td key={cell}>{cell}</td>
			))}
		</tr>
	));

	return (
		<table>
			<thead>
				<tr>{headers}</tr>
			</thead>
			<tbody>{rows}</tbody>
		</table>
	);
}

function CustomLink(props: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
	const href = props.href;

	if (href?.startsWith("/")) {
		return (
			<Link href={href} {...props}>
				{props.children}
			</Link>
		);
	}

	if (href?.startsWith("#")) {
		return <a {...props} />;
	}

	return <a target="_blank" rel="noopener noreferrer" {...props} />;
}

function RoundedImage(props: {
	alt: string;
	src: string;
	width: number;
	height: number;
}) {
	return <Image className="rounded-lg" {...props} />;
}

function slugify(str: string) {
	return str
		.toString()
		.toLowerCase()
		.trim() // Remove whitespace from both ends of a string
		.replace(/\s+/g, "-") // Replace spaces with -
		.replace(/&/g, "-and-") // Replace & with 'and'
		.replace(/[^\w\-]+/g, "") // Remove all non-word characters except for -
		.replace(/\-\-+/g, "-"); // Replace multiple - with single -
}

function createHeading(level: number) {
	const Heading = ({
		children,
		...props
	}: React.HTMLProps<HTMLHeadingElement>) => {
		const slug = slugify(children?.toString() || "");
		return React.createElement(
			`h${level}`,
			{ id: slug, className: "text-primary", ...props },
			React.createElement("a", {
				href: `#${slug}`,
				key: `link-${slug}`,
				className: "anchor text-primary",
			}),
			children,
		);
	};

	Heading.displayName = `Heading${level}`;

	return Heading;
}

function Paragraph(props: React.HTMLProps<HTMLParagraphElement>) {
	return <p {...props} className="text-primary" />;
}

function List(props: React.HTMLProps<HTMLUListElement>) {
	return <ul {...props} className="text-primary" />;
}

function ListItem(props: React.HTMLProps<HTMLLIElement>) {
	return <li {...props} className="text-primary" />;
}

function Strong(props: React.HTMLProps<HTMLSpanElement>) {
	return <strong {...props} className="text-primary" />;
}
